import { useState } from 'react';
import { socialCardKeys } from 'src/constants/socialcard.constant';
import { textColors } from 'src/constants/wall.constant';
import ClassNames from 'src/components/componentHelper/ClassNames';

const getYoutubeId = (url = '') => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^?&/]+)/);
  return match ? match[1] : '';
};

const buildYoutubeEmbedUrl = (id, autoplay) =>
  `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1${autoplay ? '&autoplay=1' : ''}`;

const getTiktokEmbedUrl = (url = '') => {
  const match = url.match(/\/video\/(\d+)/);
  return match ? `https://www.tiktok.com/player/v1/${match[1]}?music_info=0&description=0&rel=0` : '';
};

const SocialVideoCard = ({ cardItem, cardWidth, darkModeEnabled = false, cardFontColor, cardBackgroundObject = {}, fontStyle, refresh }) => {
  const platform = cardItem[socialCardKeys.sourcePlatform];
  const isTiktok = platform === 'tiktok';
  const isLoom = platform === 'loom';
  const sourceUrl = cardItem[socialCardKeys.messageLink] || '';
  const youtubeId = !isTiktok && !isLoom ? getYoutubeId(sourceUrl) : '';
  const [ytStarted, setYtStarted] = useState(false);
  // Loom's player url is resolved at import time and stored on the card, so no
  // Loom share link is ever parsed at render time.
  const embedUrl = isTiktok
    ? getTiktokEmbedUrl(sourceUrl)
    : isLoom
      ? (cardItem[socialCardKeys.videoEmbedUrl] || '')
      : (youtubeId ? buildYoutubeEmbedUrl(youtubeId, ytStarted) : '');

  if (!embedUrl) return null;

  const thumbnail = cardItem[socialCardKeys.customThumbnailUrl] || cardItem[socialCardKeys.messageVideoThumbnail];
  const ytPosterSrc = thumbnail || (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : '');
  const showYoutubePoster = !isTiktok && youtubeId && !ytStarted;
  const textColor = !darkModeEnabled ? (cardFontColor || textColors.black.primary) : (cardFontColor || textColors.light.primary);
  const messageContent = cardItem[socialCardKeys.messageContent];

  const frameStyle = {
    borderRadius: '10px',
    backgroundColor: isTiktok ? '#030712' : '#000',
    overflow: 'hidden',
  };

  const aspectStyle = {
    position: 'relative',
    aspectRatio: isTiktok ? '9 / 16' : '16 / 9',
    maxWidth: isTiktok ? '21rem' : '100%',
    width: '100%',
    margin: '0 auto',
    backgroundColor: '#000',
    overflow: 'hidden',
  };

  const iframeStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
  };

  return (
    <div
      className={ClassNames('border-none relative z-0 transition-all w-full card-holder')}
      style={{ maxWidth: '39rem', cursor: 'default', minWidth: cardWidth, borderRadius: '10px', backgroundColor: 'transparent' }}
    >
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={frameStyle}>
          <div style={aspectStyle}>
          {showYoutubePoster ? (
            <button
              type="button"
              onClick={() => setYtStarted(true)}
              aria-label="Play video"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                padding: 0,
                border: 0,
                cursor: 'pointer',
                background: '#000',
                display: 'block',
              }}
            >
              {ytPosterSrc ? (
                <img
                  src={ytPosterSrc}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onLoad={() => refresh && refresh()}
                />
              ) : null}
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '64px',
                    height: '64px',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="white" style={{ width: '28px', height: '28px', marginLeft: 3 }}>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
            </button>
          ) : (
            <iframe
              title={`${isTiktok ? 'TikTok' : isLoom ? 'Loom' : 'YouTube'} video testimonial`}
              src={embedUrl}
              style={iframeStyle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              onLoad={() => refresh && refresh()}
            />
          )}
          </div>
        </div>
      </div>

      {messageContent ? (
        <div
          className={ClassNames('px-3 pt-2 pb-3 text-left', fontStyle && fontStyle !== '' ? `font-${fontStyle}` : '')}
          style={{ color: textColor }}
        >
          <q
            className="italic"
            style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {messageContent}
          </q>
        </div>
      ) : null}
    </div>
  );
};

export default SocialVideoCard;
